import { format, getYear } from "date-fns";
import z from "zod";

import { getWeekObject } from "@lib/attendance/filters/dateRange";
import { i18n } from "@lib/i18n";
import { globalLayoutRoute } from "@routes";
import { requireScope } from "@routes/authorization";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { NETWORK_TASKS_FILTERABLE_SELECTION_STATUSES } from "./domain/networkTaskSelectionStatus";

const NETWORK_TASKS_DATE_MODE_STORAGE_KEY = "networkTasks.dateMode";
const NETWORK_TASKS_GROUP_BY_STORAGE_KEY = "networkTasks.groupBy";
const NETWORK_TASKS_SORT_BY_STORAGE_KEY = "networkTasks.sortBy";

const NetworkTasksGroupByColumnEnum = z.enum([
    "organisationName",
    "jobTitle",
    "location",
    "manager",
    "department",
]);
export type NetworkTasksGroupByColumn = z.infer<typeof NetworkTasksGroupByColumnEnum>;

const NetworkTasksSortableColumnEnum = z.enum([
    "organisationName",
    "taskName",
    "startDate",
    "jobTitle",
    "applicantsCount",
    "manager",
    "department",
]);
export type NetworkTasksSortableColumn = z.infer<typeof NetworkTasksSortableColumnEnum>;

const NetworkTasksSortByItemSchema = z.object({
    id: NetworkTasksSortableColumnEnum,
    desc: z.boolean(),
});
const NetworkTasksFilterSelectionStatusEnum = z.enum(NETWORK_TASKS_FILTERABLE_SELECTION_STATUSES);
const NetworkTasksGroupBySchema = z.array(NetworkTasksGroupByColumnEnum);
const NetworkTasksSortBySchema = z.array(NetworkTasksSortByItemSchema);

const NetworkTasksDateModeEnum = z.enum(["day", "week", "month"]);
export type NetworkTasksDateModeUnion = z.infer<typeof NetworkTasksDateModeEnum>;

function getStoredNetworkTasksJsonValue<T>(
    storageKey: string,
    schema: z.ZodType<T>,
    fallback: T,
): T {
    if (typeof window === "undefined") {
        return fallback;
    }

    const storedValue = window.localStorage.getItem(storageKey);

    if (!storedValue) {
        return fallback;
    }

    try {
        const parsedValue = JSON.parse(storedValue);
        const validatedValue = schema.safeParse(parsedValue);

        return validatedValue.success ? validatedValue.data : fallback;
    } catch {
        return fallback;
    }
}

function setStoredNetworkTasksJsonValue(storageKey: string, value: unknown) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(value));
}

function normalizeNetworkTasksGroupBy(
    groupBy: NetworkTasksGroupByColumn[],
): NetworkTasksGroupByColumn[] {
    return groupBy.length > 0 ? [groupBy[0]] : [];
}

function getDefaultNetworkTasksDateMode(): NetworkTasksDateModeUnion {
    if (typeof window === "undefined") {
        return "week";
    }

    const storedDateMode = window.localStorage.getItem(NETWORK_TASKS_DATE_MODE_STORAGE_KEY);
    const parsedDateMode = NetworkTasksDateModeEnum.safeParse(storedDateMode);

    return parsedDateMode.success ? parsedDateMode.data : "week";
}

export function setStoredNetworkTasksDateMode(dateMode: NetworkTasksDateModeUnion) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(NETWORK_TASKS_DATE_MODE_STORAGE_KEY, dateMode);
}

function getDefaultNetworkTasksGroupBy(): NetworkTasksGroupByColumn[] {
    return normalizeNetworkTasksGroupBy(
        getStoredNetworkTasksJsonValue(
            NETWORK_TASKS_GROUP_BY_STORAGE_KEY,
            NetworkTasksGroupBySchema,
            [],
        ),
    );
}

export function setStoredNetworkTasksGroupBy(groupBy: NetworkTasksGroupByColumn[]) {
    setStoredNetworkTasksJsonValue(
        NETWORK_TASKS_GROUP_BY_STORAGE_KEY,
        normalizeNetworkTasksGroupBy(groupBy),
    );
}

function getDefaultNetworkTasksSortBy(): z.infer<typeof NetworkTasksSortBySchema> {
    return getStoredNetworkTasksJsonValue(
        NETWORK_TASKS_SORT_BY_STORAGE_KEY,
        NetworkTasksSortBySchema,
        [],
    );
}

export function setStoredNetworkTasksSortBy(sortBy: z.infer<typeof NetworkTasksSortBySchema>) {
    setStoredNetworkTasksJsonValue(NETWORK_TASKS_SORT_BY_STORAGE_KEY, sortBy);
}

const NetworkTasksSearchModeEnum = z.enum(["sider", "manager"]);
export type NetworkTasksSearchModeUnion = z.infer<typeof NetworkTasksSearchModeEnum>;

const NetworkTasksSearchSchema = z.object({
    // date
    dateMode: NetworkTasksDateModeEnum.default(() => getDefaultNetworkTasksDateMode()),
    day: z.coerce.date().default(() => new Date()),
    week: z.preprocess(
        (v) => (typeof v === "string" && /^\d{1,2}-\d{4}$/.test(v) ? v : undefined),
        z.string().default(() => getWeekObject(new Date()).objectID),
    ),
    month: z.preprocess(
        (v) => (typeof v === "string" && /^\d{2}-\d{4}$/.test(v) ? v : undefined),
        z.string().default(() => `${format(new Date(), "MM")}-${getYear(new Date())}`),
    ),

    organisationIds: z.array(z.string()).default([]),
    siderIds: z.array(z.string()).default([]),
    managerIds: z.array(z.string()).default([]),

    // search
    searchMode: NetworkTasksSearchModeEnum.default("sider"),
    search: z.string().optional(),

    // Table state persisted in URL
    selectionStatuses: z.array(NetworkTasksFilterSelectionStatusEnum).default([]),
    groupBy: NetworkTasksGroupBySchema.transform(normalizeNetworkTasksGroupBy).default(() =>
        getDefaultNetworkTasksGroupBy(),
    ),
    sortBy: NetworkTasksSortBySchema.default(() => getDefaultNetworkTasksSortBy()),
});

export type NetworkTasksSearch = z.infer<typeof NetworkTasksSearchSchema>;

export const networkTasksRoute = createRoute({
    getParentRoute: () => globalLayoutRoute,
    path: "network/tasks",
    component: lazyRouteComponent(() => import("./NetworkTasks"), "NetworkTasks"),
    validateSearch: NetworkTasksSearchSchema,
    beforeLoad: () => {
        requireScope("network");
        return { getTitle: () => i18n.doc_title_tasks() };
    },
});
